import { useEffect, useMemo, useState } from 'react';
import { BookingsStatStrip } from './BookingsStatStrip';
import { BookingsToolbar } from './BookingsToolbar';
import { BookingsTable } from './BookingsTable';
import { BookingsEmpty } from './BookingsEmpty';
import { BookingsError } from './BookingsError';
import { computeStats, currentDogsByArea, statWindow } from './domain';
import { isoDate } from '../../lib/dates';
import { useWindowedBookings } from './useWindowedBookings';
import { useArrivedResidents } from './useArrivedResidents';
import { OwnerBookingModal } from '../owners/OwnerBookingModal';
import { EMPTY_BOOKINGS_FILTER } from './constants';
import { Pagination } from '../common';
import { useDeferredLoading } from '../../lib/useDeferredLoading';
import { useNav } from '../../pages/NavContext';
import { isFilterActive, listKindOf } from './filterTypes';
import type { BookingsFilter, ListRow, ListTotals, LoadState, SortKey, SortState, Viewing } from './types';

export interface BookingsViewProps {
  state: LoadState;
  // Filter is owned by the container (it drives the server query). Optional so
  // BookingsView still renders as a pure state machine in unit tests.
  filter?: BookingsFilter;
  setFilter?: (f: BookingsFilter) => void;
  // Server-side pagination of the table (20/page, shared Pagination). The two
  // figures, never summed (AC12); page/setPage drive the fetched offset.
  totals?: ListTotals | null;
  page?: number;
  setPage?: (page: number) => void;
  // Sort is owned by the container (it drives the server query). Optional so
  // BookingsView still renders standalone in unit tests.
  sort?: SortState;
  onSort?: (k: SortKey) => void;
  onRetry?: () => void;
}

const DEFAULT_SORT: SortState = { key: 'added', dir: 'desc' };

export function BookingsView({
  state,
  filter = EMPTY_BOOKINGS_FILTER,
  setFilter = () => {},
  totals = null,
  page = 1,
  setPage = () => {},
  sort = DEFAULT_SORT,
  onSort = () => {},
  onRetry,
}: BookingsViewProps) {
  // One unified owner↔booking modal — opened on a booking row (EDIT). Booking
  // CREATE now comes from the global top-bar New menu (CreateModalProvider); the
  // modal owns its own owner search + timeline.
  // The desk's own shape: a booking id, or a viewing (viewings-in-the-bookings-list
  // AC4) — a viewing row opens the viewing pane, never a booking.
  const [modal, setModal] = useState<{ bookingId: string | null; viewing?: Viewing } | null>(null);
  const today = isoDate(new Date());

  const openBooking = (id: string) => setModal({ bookingId: id });
  const openRow = (row: ListRow) =>
    row.kind === 'booking' ? openBooking(row.booking.id) : setModal({ bookingId: null, viewing: row.viewing });

  // Navigate-to-booking intent (e.g. from a booking row in the owner modal on
  // another page): open it on arrival, then clear the one-shot intent.
  const { bookingIntent, consumeBookingIntent } = useNav();
  useEffect(() => {
    if (!bookingIntent) return;
    openBooking(bookingIntent);
    consumeBookingIntent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingIntent]);
  const closeModal = () => setModal(null);
  // A commit inside the modal needs the background lists to reflect it — but NOT
  // by hard-reloading them. The mutation bus does it: api() bumps it on the
  // write, and every data hook (table, stat strip) silently revalidates in place.
  const onModalChanged = () => {};

  // Table rows are the server-filtered, server-paged set (search-bookings) —
  // already filtered, so the table renders them directly. The stat strip reads a
  // fixed "now" window instead.
  const tableRows: ListRow[] = state.phase === 'ready' ? state.rows : [];
  const kind = listKindOf(filter.types);

  const statRange = useMemo(() => statWindow(), []);
  const { bookings: statBookings } = useWindowedBookings(statRange);

  // A narrowed Type or a handed-over window is an active filter too (landmine
  // 2): an empty Viewing list must offer a way back, not claim there are no
  // bookings. (The per-status chip counts this page once fetched for a toolbar
  // it no longer has are gone with it — a hidden /bookings/stats read on every
  // load that rendered nothing; Sir, 2026-09-02.)
  const filterActive = isFilterActive(filter);

  // Anti-flicker: a quick sort/search re-fetch holds the current rows and only
  // shows the skeleton once the wait crosses the delay — no snap-on-snap-off.
  const { displayRows, showSkeleton } = useDeferredLoading(tableRows, state.phase === 'loading');

  // "Current dogs" is a LIVE headcount — it must read the full arrived set, not
  // the ±2-day window, or an uncollected overstay outside the window is dropped.
  const { bookings: arrivedBookings, loading: arrivedLoading } = useArrivedResidents(true);
  const stats = useMemo(() => {
    const flux = computeStats(statBookings);
    return arrivedLoading ? flux : { ...flux, ...currentDogsByArea(arrivedBookings) };
  }, [statBookings, arrivedBookings, arrivedLoading]);

  // The table region is the only thing tied to `state` (the list/search fetch).
  // It shows its own loading/error/empty in place — the stat strip + toolbar stay
  // put during a search re-fetch.
  // Keep the table header mounted through every phase (no full-table reload); the
  // body shows the column-matched skeleton, the held rows, or the live rows.
  const tableRegion = (() => {
    if (state.phase === 'error') return <BookingsError message={state.message} kind={kind} onRetry={onRetry} />;
    if (showSkeleton) return <BookingsTable rows={[]} kind={kind} sort={sort} onSort={onSort} loading />;
    // Keyed off displayRows, NOT `state.phase === 'ready'`. Holding a settled
    // EMPTY result through the delay only stops the skeleton flashing; the phase
    // is still 'loading' while that re-fetch is in flight, so a phase check
    // dropped the empty state and fell through to a header-only blank region —
    // trading a skeleton flash for a blank one (Codex, PR #150). Reaching this
    // line already means we are past the skeleton guard, so empty displayRows is
    // a settled empty result, live or held.
    if (displayRows.length === 0) {
      // An empty Viewing list with no other filter is the designed empty state
      // for viewings, with the way back to bookings; anything narrower offers
      // Clear filters.
      const onlyType = kind !== 'all' && !Boolean(filter.q || filter.status || filter.area || filter.inOn || filter.window);
      return filterActive && !onlyType ? (
        <BookingsEmpty variant="no-matches" kind={kind} onClearFilters={() => setFilter(EMPTY_BOOKINGS_FILTER)} />
      ) : (
        <BookingsEmpty variant="no-bookings" kind={kind} onShowBookings={() => setFilter({ ...filter, types: ['booking'], status: null })} />
      );
    }
    // Live rows, or the prior rows held during a quick re-fetch (displayRows).
    return (
      <>
        <BookingsTable rows={displayRows} kind={kind} sort={sort} onSort={onSort} onRowClick={openRow} />
        <Pagination page={page} totals={totals} kind={kind} window={filter.window} onPage={setPage} />
      </>
    );
  })();

  return (
    <div className="bookings">
      <BookingsStatStrip stats={stats} />
      <BookingsToolbar filter={filter} setFilter={setFilter} today={today} />

      {tableRegion}

      {modal && (
        <OwnerBookingModal
          bookingId={modal.bookingId}
          viewing={modal.viewing}
          seed={tableRows.flatMap((r) => (r.kind === 'booking' && r.booking.id === modal.bookingId ? [r.booking] : []))[0] ?? null}
          today={today}
          onClose={closeModal}
          onChanged={onModalChanged}
        />
      )}
    </div>
  );
}
